import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

// "Add to Home Screen" prompt for installable PWAs.
// On Chrome/Edge/Samsung Internet: hooks the `beforeinstallprompt` event and
// surfaces a native install dialog when the user taps "Install".
// On iOS Safari: shows a small inline tip (the OS doesn't expose
// `beforeinstallprompt`; A2HS is manual via Share → Add to Home Screen).
// Auto-hides after install, after the user dismisses, or if already installed.

const DISMISS_KEY = "hireloop_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 14 * 24 * 3600 * 1000; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setIosTip(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari fallback — no `beforeinstallprompt`, but still want to nudge.
    if (isIosSafari()) {
      const t = setTimeout(() => setIosTip(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setShow(false);
    setIosTip(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (!show && !iosTip) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-3 bottom-3 z-[60] md:hidden"
    >
      <div className="flex items-start gap-3 rounded-sm border border-blue-200 bg-blue-50 px-4 py-3 shadow-lg">
        <Download className="mt-0.5 h-5 w-5 flex-none text-blue-700" />
        <div className="min-w-0 flex-1 text-[13px] leading-snug text-slate-900">
          {iosTip ? (
            <>
              <strong>Install Hireloop PRO.</strong> Tap the{" "}
              <span aria-label="Share">
                <svg
                  aria-hidden
                  className="inline h-3.5 w-3.5 -translate-y-0.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13" />
                </svg>
              </span>{" "}
              <strong>Share</strong> button below and choose{" "}
              <strong>Add to Home Screen</strong> for one-tap access.
            </>
          ) : (
            <>
              <strong>Install Hireloop PRO</strong> on your home screen for
              one-tap access to your pipeline.
            </>
          )}
        </div>
        {show && (
          <button
            type="button"
            data-testid="pwa-install-accept"
            onClick={install}
            className="flex-none rounded-sm bg-blue-700 px-3 py-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-white hover:bg-blue-600"
          >
            Install
          </button>
        )}
        <button
          type="button"
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss"
          onClick={dismiss}
          className="flex-none rounded-full p-1 text-slate-500 hover:bg-blue-100 hover:text-slate-900"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
